import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconDotsVertical } from '@tabler/icons-react';

/**
 * ActionDropdown - A premium, self-contained action menu
 * 
 * @param {Array} menuItems - Array of action objects: { label, icon, onClick, danger, description, disabled }
 * @param {string} className - Optional extra classes for the trigger button
 */
export default function ActionDropdown({ menuItems = [], className = "" }) {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    // Calculate position and close on outside click
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const scrollY = window.scrollY;
            const scrollX = window.scrollX;
            const dropdownWidth = 190;
            const dropdownHeight = menuItems.length * 52 + 12; // Approximation: Item (42px) + margin/padding

            // Check if dropdown goes off the bottom of the viewport
            const spaceBelow = window.innerHeight - rect.bottom;
            const shouldOpenUp = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

            setPosition({
                top: shouldOpenUp
                    ? rect.top + scrollY - dropdownHeight - 8
                    : rect.bottom + scrollY + 8,
                left: rect.right + scrollX - dropdownWidth,
            });
        }

        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            // Handle window resize/scroll to reposition or close
            window.addEventListener('scroll', () => setIsOpen(false));
            window.addEventListener('resize', () => setIsOpen(false));
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', () => setIsOpen(false));
            window.removeEventListener('resize', () => setIsOpen(false));
        };
    }, [isOpen]);

    const handleItemClick = (item) => {
        if (item.disabled) return;
        setIsOpen(false);
        if (item.onClick) {
            item.onClick();
        }
    };

    const dropdownMenu = isOpen && createPortal(
        <div
            ref={dropdownRef}
            className="fixed w-[190px] bg-bg-dark border border-border rounded-xl shadow-2xl z-[9999] p-6"
            style={{ top: position.top, left: position.left }}
        >
            {menuItems.map((item, index) => {
                const Icon = item.icon;
                return (
                    <button
                        key={index}
                        onClick={() => handleItemClick(item)}
                        disabled={item.disabled}
                        className={`w-full group flex items-center gap-10 p-10 rounded-lg transition-all text-left mb-1 last:mb-0
                            ${item.disabled ? 'opacity-40 cursor-not-allowed' :
                                item.danger
                                    ? 'hover:bg-danger/10 text-danger'
                                    : 'hover:bg-primary/10 text-text-main hover:text-primary'}`}
                    >
                        <div className={`w-30 h-30 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110 duration-200
                            ${item.danger ? 'bg-danger/10' : 'bg-primary/10'}`}>
                            {Icon && <Icon size={16} className={item.danger ? 'text-danger' : 'text-primary'} />}
                        </div>
                        <div className="flex-grow">
                            <p className="font-semibold text-[13px] leading-tight">{item.label}</p>
                            {item.description && (
                                <p className="text-[10px] opacity-60 mt-2 line-clamp-1">{item.description}</p>
                            )}
                        </div>
                    </button>
                );
            })}
        </div>,
        document.body
    );

    return (
        <>
            <button
                ref={buttonRef}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className={`p-10 hover:bg-bg-light border border-transparent hover:border-border rounded-lg transition-all text-text-secondary hover:text-primary group ${className} ${isOpen ? 'bg-bg-light border-border text-primary' : ''}`}
                title="More Actions"
            >
                <IconDotsVertical
                    size={20}
                    className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : 'group-hover:rotate-90'}`}
                />
            </button>
            {dropdownMenu}
        </>
    );
}
